/**
 * Create / edit a bucket group (FE-3, PHASE1 §4 bucket-groups).
 *
 * The stateful body is mounted only while open, so state initialises fresh from
 * the current `group` prop each time (no reset effect).
 */
import { useState } from 'react';
import { Modal } from '../../components/ui/Modal.tsx';
import { InlineError } from '../../components/ui/InlineError.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { Field } from '../../components/ui/Field.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { ApiRequestError } from '../../lib/api.ts';
import { useCreateBucketGroup, useUpdateBucketGroup } from '../../lib/hooks/useBucketMutations.ts';
import type { BucketGroupOut } from '../../lib/types.ts';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export interface BucketGroupFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode; absent/null → create mode. */
  group?: BucketGroupOut | null;
}

export function BucketGroupFormDialog({ open, onOpenChange, group }: BucketGroupFormDialogProps) {
  if (!open) return null;
  return <BucketGroupForm group={group ?? null} onClose={() => onOpenChange(false)} />;
}

function BucketGroupForm({
  group,
  onClose,
}: {
  group: BucketGroupOut | null;
  onClose: () => void;
}) {
  const isEdit = group !== null;
  const create = useCreateBucketGroup();
  const update = useUpdateBucketGroup();

  const [name, setName] = useState(() => group?.name ?? '');
  const [icon, setIcon] = useState(() => group?.icon ?? '');
  const [color, setColor] = useState(() => group?.color ?? '');
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;

  function handleSubmit() {
    setError(null);
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('Bitte einen Namen angeben.');
      return;
    }
    const trimmedIcon = icon.trim();
    const trimmedColor = color.trim();

    const onError = (err: unknown) =>
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Speichern fehlgeschlagen. Bitte erneut versuchen.',
      );

    const body = {
      name: trimmedName,
      icon: trimmedIcon === '' ? null : trimmedIcon,
      color: trimmedColor === '' ? null : trimmedColor,
    };

    if (isEdit && group) {
      update.mutate({ id: group.id, body }, { onSuccess: onClose, onError });
    } else {
      create.mutate(body, { onSuccess: onClose, onError });
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={isEdit ? 'Gruppe bearbeiten' : 'Neue Gruppe'}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={pending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineError message={error} />

        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Fixe Kosten"
              autoFocus
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Symbol" hint="Emoji, optional">
            {(id) => (
              <Input
                id={id}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="📌"
                maxLength={4}
              />
            )}
          </Field>
          <Field label="Farbe" hint="Hex, optional">
            {(id) => (
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Farbe wählen"
                  value={HEX_RE.test(color) ? color : '#1565c0'}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-slate-200 bg-white transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:bg-slate-900 dark:focus-visible:outline-sky-500"
                />
                <Input
                  id={id}
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#1565C0"
                />
              </div>
            )}
          </Field>
        </div>
      </div>
    </Modal>
  );
}
